import { useRegionContext } from 'context/RegionContext';
import { MapSearchContext } from 'context/MapSearchContext';
import { SearchStatus } from 'context/MapSearchContext/types';
import { getResultCount, hasPathsStartingFromStation } from 'context/MapSearchContext/utils';
import { ExpandedStation } from 'pages/Map/types';
import { useContext } from 'react';
import { useTheme } from 'styled-components';

export const useMapSearch = () => {
    const { zoneWiseResult, activeSearchId } = useContext(MapSearchContext);
    const { currentZone } = useRegionContext();
    const theme = useTheme();
    const SEARCH_COLOR = {
        [SearchStatus.SEARCH_ACTIVE]: theme.colors.royalBlue[500],
        [SearchStatus.SEARCH_FOUND]: theme.colors.grey[600]
    };

    // get the search result of item by id
    const getSearchResult = (id: string) => {
        return zoneWiseResult[currentZone.name] && zoneWiseResult[currentZone.name][id];
    };

    // returns if id has search result or not
    const hasSearchResult = (id: string) => {
        return !!getSearchResult(id);
    };

    // returns if id has own search result or not
    const hasOwnSearchResult = (id: string) => {
        return !!getSearchResult(id)?.words;
    };

    const hasSubSearchResult = (station: ExpandedStation) => {
        if (!station) return false;

        return station
            .getStations()
            .some(station => hasSearchResult(station.id) || hasPathsStartingFromStation(station.id, zoneWiseResult[currentZone.name]));
    };

    // gets search status (whether id is part of search or not and is current active search item)
    const getSearchStatus = (id: string, station?: ExpandedStation) => {
        if (activeSearchId === id) {
            return SearchStatus.SEARCH_ACTIVE;
        }

        if (hasOwnSearchResult(id) || hasSubSearchResult(station)) {
            return SearchStatus.SEARCH_FOUND;
        }

        return null;
    };

    // gets search color based on status
    const getSearchColor = (id: string, station?: ExpandedStation) => {
        const status = getSearchStatus(id, station);
        return status ? SEARCH_COLOR[status] : null;
    };

    // returns is currently acitve search item
    const isCurrentlySearchingItem = (id: string) => {
        return getSearchStatus(id) === SearchStatus.SEARCH_ACTIVE;
    };

    const getZoneSearchCount = (zone: string) => {
        return getResultCount(zoneWiseResult, zone);
    };

    const getSearchWords = (id: string) => {
        return Object.keys(getSearchResult(id)?.words ?? {});
    };

    return {
        getSearchResult,
        hasSearchResult,
        hasOwnSearchResult,
        getSearchStatus,
        getSearchColor,
        getZoneSearchCount,
        getSearchWords,
        isCurrentlySearchingItem
    };
};
